// src/components/ui/DownloadCompleteModal.js
import React, { useState, useEffect } from 'react';
import { Check, FolderLock, SquarePen, Trash2, ThumbsUp, Save } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import ThreeDotLoader from './ThreeDotLoader';
import DocumentSetPill from '../document-sets/DocumentSetPill';
import GenericToggle from './GenericToggle';
import { ReactComponent as BlobIcon } from '../../assets/icons/blob.svg';
import { ReactComponent as TagCreate } from '../../assets/icons/tag-create.svg';
import DocumentSetInlineEditor from '../document-sets/DocumentSetInlineEditor';

/**
 * Custom Icon Button for Pending Tag Pill - matches sidebar pill hover behavior
 */
const PendingTagIconButton = ({ children, onClick, title, hoverColor, defaultColor, hoverTextColor }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="p-1 rounded-app-2xs flex items-center justify-center flex-shrink-0"
      style={{
        backgroundColor: isHovered ? hoverColor : 'transparent',
        color: isHovered ? hoverTextColor : defaultColor,
        opacity: isHovered ? 1 : 0.7
      }}
      title={title}
      aria-label={title}
    >
      {React.cloneElement(children, {
        className: 'w-3 h-3'
      })}
    </button>
  );
};

/**
 * Download Complete Modal
 * Shows download progress, automation features, and archive options
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {string} caseName - Name of the case being downloaded
 * @param {object} documentSet - Document set object (optional)
 * @param {boolean} isDownloading - Whether download is in progress
 * @param {function} onArchiveSet - Callback when archive set toggle changes (setId, shouldArchive)
 * @param {function} onArchiveCase - Callback when archive case toggle changes (shouldArchive)
 * @param {array} documentSets - Array of all document sets for validation
 * @param {function} onCreateDocumentSet - Callback to create a new tag and link with documents
 * @param {array} allDocumentIds - Array of all document IDs being downloaded
 */
const DownloadCompleteModal = ({
  isOpen,
  onClose,
  caseName = '',
  documentSet = null,
  isDownloading = false,
  onArchiveSet,
  onArchiveCase,
  documentSets = [],
  onCreateDocumentSet,
  allDocumentIds = [],
}) => {
  // Local state for toggles - only apply changes on close
  const [archiveSetEnabled, setArchiveSetEnabled] = useState(false);
  const [archiveCaseEnabled, setArchiveCaseEnabled] = useState(false);

  // Local state for tag creation - store pending tag data, only create on Done
  const [pendingTagData, setPendingTagData] = useState(null); // { name, color }
  const [isEditingTag, setIsEditingTag] = useState(false);
  const [isEditorFocused, setIsEditorFocused] = useState(false);

  // Reset toggles and tag creation state when modal opens
  useEffect(() => {
    if (isOpen) {
      setArchiveSetEnabled(false);
      setArchiveCaseEnabled(false);
      setPendingTagData(null);
      setIsEditingTag(false);
    }
  }, [isOpen]);

  // Handle close without saving (X button or clicking away)
  const handleClose = (open) => {
    if (!open) {
      onClose();
    }
  };

  // Check if there are any changes that need to be saved
  const hasChanges = pendingTagData || archiveSetEnabled || archiveCaseEnabled;

  // Handle Done button - save changes and close
  const handleDone = async () => {
    // If there's a pending tag, create it now
    if (pendingTagData && onCreateDocumentSet) {
      try {
        console.log('DownloadCompleteModal: Creating tag on Done', pendingTagData);
        const newTag = await onCreateDocumentSet(pendingTagData.name, pendingTagData.color, allDocumentIds);
        console.log('DownloadCompleteModal: Tag created', newTag);

        // Apply archive toggle to the newly created tag if enabled
        if (archiveSetEnabled && onArchiveSet) {
          onArchiveSet(newTag.id, true);
        }
      } catch (error) {
        console.error('Error creating tag:', error);
        // Don't close modal if tag creation failed
        return;
      }
    } else if (archiveSetEnabled && documentSet && onArchiveSet) {
      // Archive existing documentSet if no new tag was created
      onArchiveSet(documentSet.id, true);
    }

    // Apply case archive toggle
    if (archiveCaseEnabled && onArchiveCase) {
      onArchiveCase(true);
    }

    onClose();
  };

  // Handle tag input from inline editor - just store locally, don't create yet
  const handleCreateTag = (name, color) => {
    console.log('DownloadCompleteModal: Tag data entered', { name, color });
    setPendingTagData({ name, color });
    setIsEditingTag(false);
  };

  // Handle updating the pending tag (when editing)
  const handleUpdatePendingTag = (id, name) => {
    console.log('DownloadCompleteModal: Updating pending tag', { name });
    setPendingTagData(prev => ({ ...prev, name }));
    setIsEditingTag(false);
  };

  // Handle editing the pending tag
  const handleEditTag = () => {
    setIsEditingTag(true);
  };

  // Handle deleting the pending tag
  const handleDeleteTag = () => {
    setPendingTagData(null);
    setIsEditingTag(false);
    setIsEditorFocused(false); // Reset editor focus state when deleting
  };

  // Handle canceling tag creation
  const handleCancelTagCreation = () => {
    if (!pendingTagData) {
      // If no tag data yet, do nothing (leave editor open)
    } else {
      // If editing existing pending tag, stop editing
      setIsEditingTag(false);
    }
  };

  // Handle focus state changes from the inline editor
  const handleEditorFocusChange = (focused) => {
    setIsEditorFocused(focused);
  };

  // Determine which tag to display in header (show existing documentSet OR pending tag)
  const displayTag = documentSet;
  const shouldShowTagCreationPrompt = !documentSet;

  const features = [
    'Stamped documents',
    'Converted all files to .pdf',
    'Generated Bates index',
    'Made PDF searchable',
    'Paginated documents',
    'Standardized document sizes'
  ];

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Portal>
        {/* Backdrop with blur */}
        <Dialog.Overlay
          className="fixed inset-0 z-[200] bg-black/40"
          style={{
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)'
          }}
        />

        {/* Modal Container */}
        <div className="fixed left-1/2 top-1/2 z-[201] -translate-x-1/2 -translate-y-1/2">
          {/* Close Button */}
          <Dialog.Close asChild>
            <button
              className="group absolute w-comp-3xl h-comp-3xl rounded-full border border-app-gray-900 bg-app-white hover:bg-app-gray-300 transition-colors duration-app-normal flex items-center justify-center z-10"
              title="Close"
              style={{
                top: '-10px',
                right: '-10px',
                pointerEvents: 'auto',
                transform: 'translateZ(0)',
                willChange: 'transform'
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                className="text-app-gray-900"
                style={{ strokeWidth: 1 }}
              >
                <path
                  d="M1 1L11 11M11 1L1 11"
                  stroke="currentColor"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </Dialog.Close>

          {/* Modal Content */}
          <Dialog.Content
            className="relative bg-app-white rounded-app-lg border border-app-gray-900 p-app-3xl focus:outline-none"
            style={{
              width: '600px',
              pointerEvents: 'auto'
            }}
          >
            {/* Title Section */}
            <div className="flex items-center justify-between mb-app-2xl">
              {/* Left: Downloading/Downloaded with loader */}
              <div className="flex items-center gap-app-sm">
                <h2 className="text-app-2xl font-medium text-app-gray-900 mr-app-xl">
                  {isDownloading ? 'Downloading' : 'Success!'}
                </h2>
                {isDownloading && <ThreeDotLoader size={6} />}
              </div>

              {/* Right: Case name and document set */}
              <div className="flex items-center text-app-gray-700">
                <div className="px-app-sm py-app-xs bg-app-gray-400 border border-app-gray-800 rounded-app-sm flex items-center gap-app-xl">
                  <span className="text-app-sm font-medium text-app-gray-800">{caseName}</span>
                  {displayTag ? (
                    <DocumentSetPill
                      documentSet={displayTag}
                      showRemove={false}
                      isReadOnly={true}
                    />
                  ) : pendingTagData && (
                    <div
                      className="inline-flex items-center py-1 px-3 rounded-full text-app-xs font-medium border"
                      style={{
                        backgroundColor: pendingTagData.color.bg,
                        color: pendingTagData.color.text,
                        borderColor: pendingTagData.color.border || pendingTagData.color.bg,
                      }}
                    >
                      {pendingTagData.name}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Dividing line */}
            <div className="mb-app-lg border-t border-app-gray-300"></div>

            {/* Features Section - 2 columns x 3 rows */}
            <div className="mb-app-lg">
              <div className="grid grid-cols-2 gap-x-app-0 gap-y-app-md mx-app-md">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-app-sm">
                    <div className="relative w-[18px] h-[18px] flex-shrink-0 flex items-center justify-center">
                      <BlobIcon className="w-[18px] h-[18px] text-app-gray-900 absolute" />
                      <Check className="w-[9px] h-[9px] mt-[1px] text-app-white relative z-10" strokeWidth={6} />
                    </div>
                    <span className="text-app-sm text-app-gray-600">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tag Creation Prompt Section - only show if no tag selected initially */}
            {shouldShowTagCreationPrompt && (
              <div className="space-y-app-md pt-app-lg pb-app-lg border-t border-app-gray-300">
                <div>
                  <h3 className="text-app-sm font-medium text-app-gray-900">
                    Try tagging these docs!
                  </h3>
                  <p className="text-app-xs italic text-app-gray-500 mt-app-xs">
                    Are these documents for a motion, a trial, or discovery? Tag them for future reference!
                  </p>
                </div>

                {/* Show inline editor if no pending tag or if editing */}
                {(!pendingTagData || isEditingTag) ? (
                  <div className="flex items-center gap-app-sm">
                    <TagCreate
                      className={`w-comp-md h-comp-md transition-colors ${
                        isEditorFocused ? 'fill-app-success' : 'text-app-gray-800'
                      }`}
                      style={{ strokeWidth: isEditorFocused ? 3 : 2 }}
                    />
                    <DocumentSetInlineEditor
                      onCreateSet={handleCreateTag}
                      onUpdateSet={handleUpdatePendingTag}
                      onCancel={handleCancelTagCreation}
                      onFocusChange={handleEditorFocusChange}
                      documentSets={documentSets}
                      autoFocus={false}
                      saveOnBlur={false}
                      existingSet={isEditingTag && pendingTagData ? { name: pendingTagData.name, color: pendingTagData.color } : null}
                    />
                  </div>
                ) : (
                  /* Show pending tag pill with edit/delete buttons */
                  <div className="flex items-center gap-app-sm">
                    <TagCreate className="w-comp-md h-comp-md text-app-gray-800" style={{ strokeWidth: 2 }} />
                    <div
                      className="group relative inline-flex px-3 items-center rounded-full text-app-xs font-medium border min-h-[22px]"
                      style={{
                        backgroundColor: pendingTagData.color.bg,
                        color: pendingTagData.color.text,
                        borderColor: pendingTagData.color.border || pendingTagData.color.bg,
                      }}
                    >
                      <span className="select-none truncate min-w-0 py-1 pr-[46px]">
                        {pendingTagData.name}
                      </span>

                      {/* Action buttons - absolutely positioned to overlay on right side */}
                      <div className="absolute right-2 flex items-center gap-0 py-1 flex-shrink-0">
                        <PendingTagIconButton
                          onClick={handleEditTag}
                          title="Edit tag"
                          hoverColor={pendingTagData.color.hover}
                          defaultColor={pendingTagData.color.text}
                          hoverTextColor={pendingTagData.color.text}
                        >
                          <SquarePen />
                        </PendingTagIconButton>
                        <PendingTagIconButton
                          onClick={handleDeleteTag}
                          title="Delete tag"
                          hoverColor={pendingTagData.color.hover}
                          defaultColor={pendingTagData.color.text}
                          hoverTextColor={pendingTagData.color.text}
                        >
                          <Trash2 />
                        </PendingTagIconButton>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Archive Section */}
            <div className={`space-y-app-lg ${shouldShowTagCreationPrompt ? 'pt-app-lg' : 'pt-app-2xl'} border-t border-app-gray-300`}>
              {/* Archive Document Set - show for existing documentSet OR pending tag */}
              {(displayTag || pendingTagData) && (
                <div className="flex items-start gap-app-xl">
                  <div className="mt-[1px]">
                    <GenericToggle
                      checked={archiveSetEnabled}
                      onCheckedChange={setArchiveSetEnabled}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-app-lg mb-app-xs">
                      <span className="text-app-sm font-medium text-app-gray-900">Archive:</span>
                      {displayTag ? (
                        <DocumentSetPill
                          documentSet={displayTag}
                          showRemove={false}
                          isReadOnly={true}
                        />
                      ) : (
                        <div
                          className="inline-flex items-center py-1 px-3 rounded-full text-app-xs font-medium border"
                          style={{
                            backgroundColor: pendingTagData.color.bg,
                            color: pendingTagData.color.text,
                            borderColor: pendingTagData.color.border || pendingTagData.color.bg,
                          }}
                        >
                          {pendingTagData.name}
                        </div>
                      )}
                    </div>
                    <p className="text-app-xs italic text-app-gray-500">
                      This tag will move to the case's <FolderLock className="w-4 h-4 inline-block align-text-bottom text-app-gray-900 mx-app-2xs" /> <span className="text-app-gray-700 font-semibold"> completed tag folder</span>. It preserves this set of documents for reference. You can always reactivate it!
                    </p>
                  </div>
                </div>
              )}

              {/* Archive Case */}
              <div className="flex items-start gap-app-xl">
                <div className="mt-[6px]">
                  <GenericToggle
                    checked={archiveCaseEnabled}
                    onCheckedChange={setArchiveCaseEnabled}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-app-lg mb-app-xs">
                    <span className="text-app-sm text-app-gray-900 font-medium">Archive:</span>
                    <div className="px-app-sm py-app-xs bg-app-gray-400 border border-app-gray-800 rounded-app-sm flex items-center">
                      <span className="text-app-sm font-medium text-app-gray-800">{caseName}</span>
                    </div>
                  </div>
                  <p className="text-app-xs italic text-app-gray-500">
                    This case will move to the <span className="text-app-gray-700 font-semibold">case archive tab</span>. You can always reactivate it!
                  </p>
                </div>
              </div>
            </div>

            {/* Done Button */}
            <div className="flex justify-end mt-app-2xl">
              <button
                onClick={handleDone}
                className="btn-primary"
              >
                {hasChanges ? <Save size={16} /> : <ThumbsUp size={16} />}
                {hasChanges ? 'Save' : 'Done'}
              </button>
            </div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DownloadCompleteModal;
